import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import SuccessPage from './pages/SuccessPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import TermsSalesPage from './pages/TermsSalesPage';
import ShippingReturnsPage from './pages/ShippingReturnsPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminMenuPage from './pages/admin/AdminMenuPage';
import AdminMenuItemEditPage from './pages/admin/AdminMenuItemEditPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminProductEditPage from './pages/admin/AdminProductEditPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminPizzeriaOrdersPage from './pages/admin/AdminPizzeriaOrdersPage';
import { apiPost } from './lib/api';
import {
  ANALYTICS_CONSENT_DENIED,
  ANALYTICS_CONSENT_GRANTED,
  ANALYTICS_CONSENT_UNSET,
  denyAnalyticsConsent,
  getStoredAnalyticsConsent,
  grantAnalyticsConsent,
  initializeAnalyticsConsent,
  isAnalyticsConfigured,
  resetAnalyticsConsent,
  trackEvent,
  trackPageView
} from './lib/analytics';
import {
  clearCheckoutPending,
  consumeCheckoutPending,
  getCheckoutSessionIdFromLocation,
  markCheckoutPending
} from './lib/checkout';
import {
  clearCart,
  getCartCount,
  getCartTotals,
  loadCart,
  removeCartItem,
  saveCart,
  setCartItemQty,
  toCheckoutItems,
  upsertCartItem
} from './lib/cart';
import { isCheckoutEnabled, isShopEnabled } from './lib/feature-flags';
import { LEGAL_TEXT_VERSION } from './lib/legal-config';
import { persistCheckoutLegalAcceptance, readCheckoutLegalAcceptance } from './lib/legal-checkout';

function toEuro(cents) {
  const value = Number(cents);
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, value) / 100;
}

function mapCartItemsToAnalyticsItems(items) {
  return items.map((item) => ({
    item_id: item.id,
    item_name: item.name,
    price: toEuro(item.price_cents),
    quantity: Math.max(1, Number(item.qty) || 1)
  }));
}

function RouteAnalyticsTracker() {
  const location = useLocation();
  const lastPathRef = useRef('');

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;
    if (lastPathRef.current === currentPath) {
      return;
    }

    trackPageView(currentPath);
    lastPathRef.current = currentPath;
  }, [location.pathname, location.search]);

  return null;
}

function shouldShowShopCartFab(pathname) {
  if (!isShopEnabled) {
    return false;
  }

  if (pathname === '/shop') {
    return true;
  }

  if (!pathname.startsWith('/shop/')) {
    return false;
  }

  if (pathname === '/shop/cart' || pathname === '/shop/success') {
    return false;
  }

  return true;
}

function ShopCartFab({ cartCount }) {
  const location = useLocation();
  if (!shouldShowShopCartFab(location.pathname)) {
    return null;
  }

  const countLabel = cartCount === 1 ? '1 articolo' : `${cartCount} articoli`;

  return (
    <Link className="shop-cart-fab" to="/shop/cart" aria-label={`Apri carrello (${countLabel})`}>
      <span className="shop-cart-fab-label">Carrello</span>
      <span className="shop-cart-fab-badge">{cartCount}</span>
    </Link>
  );
}

function CookieConsentBanner({ visible, onAccept, onReject }) {
  if (!visible) {
    return null;
  }

  return (
    <section className="cookie-consent-banner" role="dialog" aria-label="Consenso cookie analytics">
      <div className="cookie-consent-copy">
        <h2>Cookie analytics</h2>
        <p>
          Usiamo cookie tecnici sempre attivi. I cookie analytics sono opzionali e ci aiutano a migliorare shop e menu
          pizzeria.
        </p>
      </div>
      <div className="cookie-consent-actions">
        <button className="cookie-consent-btn cookie-consent-btn-secondary" type="button" onClick={onReject}>
          Solo necessari
        </button>
        <button className="cookie-consent-btn cookie-consent-btn-primary" type="button" onClick={onAccept}>
          Accetta analytics
        </button>
      </div>
    </section>
  );
}

function App() {
  const [cartItems, setCartItems] = useState(() => loadCart());
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutLegalAccepted, setCheckoutLegalAccepted] = useState(() =>
    readCheckoutLegalAcceptance(LEGAL_TEXT_VERSION)
  );
  const [checkoutLegalError, setCheckoutLegalError] = useState('');
  const [analyticsConsent, setAnalyticsConsent] = useState(() => getStoredAnalyticsConsent());
  const analyticsConfigured = isAnalyticsConfigured();
  const shouldShowCookieBanner = analyticsConfigured && analyticsConsent === ANALYTICS_CONSENT_UNSET;

  useEffect(() => {
    const storedConsent = initializeAnalyticsConsent();
    setAnalyticsConsent(storedConsent);
  }, []);

  useEffect(() => {
    saveCart(cartItems);
  }, [cartItems]);

  const cartCount = useMemo(() => getCartCount(cartItems), [cartItems]);
  const cartTotals = useMemo(() => getCartTotals(cartItems), [cartItems]);

  const handleAddToCart = (product, qty = 1) => {
    setCheckoutError('');
    setCheckoutLegalError('');
    setCartItems((current) => upsertCartItem(current, product, qty));

    const safeQty = Math.max(1, Number(qty) || 1);
    trackEvent('add_to_cart', {
      currency: 'EUR',
      value: toEuro((Number(product?.price_cents) || 0) * safeQty),
      items: [
        {
          item_id: product?.id,
          item_name: product?.name,
          price: toEuro(product?.price_cents),
          quantity: safeQty
        }
      ]
    });
  };

  const handleUpdateQty = (productId, qty) => {
    setCheckoutError('');
    setCheckoutLegalError('');
    setCartItems((current) => setCartItemQty(current, productId, qty));
  };

  const handleRemoveItem = (productId) => {
    setCheckoutError('');
    setCheckoutLegalError('');
    setCartItems((current) => removeCartItem(current, productId));
  };

  const handleClearCart = () => {
    setCheckoutError('');
    setCheckoutLegalError('');
    clearCheckoutPending();
    clearCart();
    setCartItems([]);
  };

  const handleCheckoutSuccess = () => {
    const pendingCheckout = consumeCheckoutPending();
    const checkoutSessionId = getCheckoutSessionIdFromLocation();
    const shouldClearCart = Boolean(pendingCheckout) || Boolean(checkoutSessionId);
    const checkoutItems = mapCartItemsToAnalyticsItems(cartItems);

    setCheckoutError('');
    setCheckoutLegalError('');
    setIsCheckoutLoading(false);

    if (shouldClearCart) {
      clearCart();
      setCartItems([]);
    }

    return {
      didClear: shouldClearCart,
      checkoutSessionId,
      pendingCheckout,
      items: checkoutItems
    };
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0 || isCheckoutLoading) {
      return;
    }

    if (!isCheckoutEnabled) {
      setCheckoutError('Checkout temporaneamente non disponibile.');
      return;
    }

    if (!checkoutLegalAccepted) {
      setCheckoutLegalError(
        'Per continuare devi accettare Termini di vendita, Privacy policy e Spedizioni e resi.'
      );
      return;
    }

    setCheckoutError('');
    setCheckoutLegalError('');
    setIsCheckoutLoading(true);

    try {
      const analyticsItems = mapCartItemsToAnalyticsItems(cartItems);
      trackEvent('begin_checkout', {
        currency: 'EUR',
        value: toEuro(cartTotals.subtotalCents),
        items: analyticsItems
      });

      const payload = await apiPost('/api/checkout/session', {
        items: toCheckoutItems(cartItems)
      });

      if (!payload?.checkoutUrl) {
        throw new Error('Checkout URL non disponibile');
      }

      markCheckoutPending({
        orderId: payload.orderId,
        subtotalCents: cartTotals.subtotalCents,
        itemCount: cartCount,
        legalVersion: LEGAL_TEXT_VERSION
      });

      trackEvent('checkout_redirect', {
        order_id: payload.orderId || undefined,
        currency: 'EUR',
        value: toEuro(cartTotals.subtotalCents),
        item_count: cartCount
      });

      window.location.assign(payload.checkoutUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore durante la creazione del checkout';
      setCheckoutError(message);
      setIsCheckoutLoading(false);
    }
  };

  const handleAcceptAnalyticsCookies = () => {
    grantAnalyticsConsent();
    setAnalyticsConsent(ANALYTICS_CONSENT_GRANTED);
    trackPageView(`${window.location.pathname}${window.location.search}`);
  };

  const handleRejectAnalyticsCookies = () => {
    denyAnalyticsConsent();
    setAnalyticsConsent(ANALYTICS_CONSENT_DENIED);
  };

  const handleLegalAcceptanceChange = (accepted) => {
    const normalized = Boolean(accepted);
    persistCheckoutLegalAcceptance(normalized, LEGAL_TEXT_VERSION);
    setCheckoutLegalAccepted(normalized);
    setCheckoutLegalError('');
  };

  const handleOpenCookiePreferences = () => {
    resetAnalyticsConsent();
    setAnalyticsConsent(ANALYTICS_CONSENT_UNSET);
  };

  return (
    <BrowserRouter>
      <RouteAnalyticsTracker />
      {!shouldShowCookieBanner ? <ShopCartFab cartCount={cartCount} /> : null}
      <CookieConsentBanner
        visible={shouldShowCookieBanner}
        onAccept={handleAcceptAnalyticsCookies}
        onReject={handleRejectAnalyticsCookies}
      />
      <div className="App">
        <header className="App-header">
          <h1>Panetteria Primavera</h1>
          <p>Autentica Pizza Italiana e prodotti locali</p>
          <nav className="App-nav" aria-label="Main navigation">
            <Link className="App-nav-link" to="/">
              Menu pizzeria
            </Link>
            {isShopEnabled && (
              <>
                <Link className="App-nav-link" to="/shop">
                  Shop prodotti
                </Link>
                <Link className="App-nav-link" to="/shop/cart">
                  Carrello ({cartCount})
                </Link>
              </>
            )}
          </nav>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            {isShopEnabled ? (
              <>
                <Route
                  path="/shop"
                  element={
                    <ShopPage
                      onAddToCart={handleAddToCart}
                      cartItems={cartItems}
                      onUpdateQty={handleUpdateQty}
                    />
                  }
                />
                <Route path="/shop/:slug" element={<ProductPage onAddToCart={handleAddToCart} />} />
                <Route
                  path="/shop/cart"
                  element={
                    <CartPage
                      cartItems={cartItems}
                      cartTotals={cartTotals}
                      onUpdateQty={handleUpdateQty}
                      onRemoveItem={handleRemoveItem}
                      onClearCart={handleClearCart}
                      onCheckout={handleCheckout}
                      checkoutEnabled={isCheckoutEnabled}
                      legalAccepted={checkoutLegalAccepted}
                      onLegalAcceptedChange={handleLegalAcceptanceChange}
                      legalError={checkoutLegalError}
                      isCheckoutLoading={isCheckoutLoading}
                      checkoutError={checkoutError}
                    />
                  }
                />
                <Route path="/shop/success" element={<SuccessPage onCheckoutSuccess={handleCheckoutSuccess} />} />
              </>
            ) : (
              <>
                <Route path="/shop" element={<Navigate replace to="/" />} />
                <Route path="/shop/:slug" element={<Navigate replace to="/" />} />
                <Route path="/shop/cart" element={<Navigate replace to="/" />} />
                <Route path="/shop/success" element={<Navigate replace to="/" />} />
              </>
            )}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookie" element={<CookiePolicyPage />} />
            <Route path="/termini-vendita" element={<TermsSalesPage />} />
            <Route path="/spedizioni-resi" element={<ShippingReturnsPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate replace to="orders" />} />
              <Route path="orders" element={<AdminOrdersPage />} />
              <Route path="pizzeria-orders" element={<AdminPizzeriaOrdersPage />} />
              <Route path="products" element={<AdminProductsPage />} />
              <Route path="products/:productId/edit" element={<AdminProductEditPage />} />
              <Route path="menu" element={<AdminMenuPage />} />
              <Route path="menu/:itemId/edit" element={<AdminMenuItemEditPage />} />
            </Route>
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </main>
        <footer className="App-footer">
          <p>&copy; {new Date().getFullYear()} Panetteria Primavera. Tutti i diritti riservati.</p>
          <p>Via Portofino, 20, 73057 Taviano LE | Tel: +39 08331624313</p>
          <nav className="App-footer-links" aria-label="Link legali">
            <Link to="/privacy">Privacy</Link>
            <Link to="/cookie">Cookie</Link>
            <Link to="/termini-vendita">Termini di vendita</Link>
            <Link to="/spedizioni-resi">Spedizioni e resi</Link>
            <button type="button" className="App-footer-link-button" onClick={handleOpenCookiePreferences}>
              Preferenze cookie
            </button>
          </nav>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;
